import { useState, useMemo, useRef, useEffect, type ChangeEvent, type KeyboardEvent } from 'react';
import {
  Heart, Moon, Scale, Smile, Meh, Check, Calendar,
  Minus, Plus, Zap, BatteryLow,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { useUserProfile, useWeightRecords, type IDailyHealthRecord } from '@/hooks/useAppState';
import { useDailyHealth } from '@/hooks/useDailyHealth';
import { formatLocalDate } from '@/lib/utils';
import HealthCalendarSheet from './HealthCalendarSheet';
import StatCard from './HealthStatCard';
import { getCurrentUserId } from '@/lib/user-storage';

interface HealthCheckInSheetProps {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

// 存储仍用 great/normal/poor 三值；UI 展示更丰富的表情选项，最终映射回三值
type MoodValue = 'great' | 'calm' | 'tired';
type MoodStored = 'great' | 'normal' | 'poor';

// 历史值（已下线）→ 当前值的兼容映射，用于展示已录入的历史数据
const LEGACY_MOOD_MAP: Record<string, MoodValue> = {
  anxious: 'tired', // 焦虑 → 疲惫（同属负面感受区）
  poor: 'tired',    // 低落 → 疲惫
};

const MOOD_OPTIONS: { value: MoodValue; label: string; icon: typeof Smile; color: string; stored: MoodStored }[] = [
  { value: 'great', label: '很好', icon: Smile, color: 'text-success border-success bg-success/10', stored: 'great' },
  { value: 'calm', label: '平静', icon: Meh, color: 'text-warning border-warning bg-warning/10', stored: 'normal' },
  { value: 'tired', label: '疲惫', icon: BatteryLow, color: 'text-muted-foreground border-muted bg-muted', stored: 'normal' },
];

function getMoodMeta(value: MoodValue | string | '') {
  const opt = MOOD_OPTIONS.find(o => o.value === value);
  if (opt) return { label: opt.label, icon: opt.icon, color: opt.color.split(' ')[0] };
  // 历史已下线值：返回原值标识 + 默认样式，保证历史数据仍可展示
  const legacyLabel: Record<string, string> = {
    anxious: '焦虑',
    poor: '低落',
  };
  if (value && legacyLabel[value]) {
    return { label: legacyLabel[value], icon: Meh, color: 'text-muted-foreground' };
  }
  return { label: '心情', icon: Smile, color: 'text-muted-foreground' };
}

// 体重输入组件 — 直接输入数字 + 步进微调（±0.1 / ±0.5）
function WeightInput({
  value,
  onChange,
  min,
  max,
  unit,
  decimal,
  empty,
  initialValue,
  onClear,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
  decimal: number;
  empty: boolean;
  initialValue: number;
  onClear?: () => void;
}) {
  const [draft, setDraft] = useState(empty ? '' : value.toFixed(decimal));
  const inputRef = useRef<HTMLInputElement>(null);

  // 外部 value 变化时同步（步进按钮触发的 onChange 会回传新值）
  useEffect(() => {
    if (document.activeElement !== inputRef.current) {
      setDraft(empty ? '' : value.toFixed(decimal));
    }
  }, [value, empty, decimal]);

  const commitValue = (raw: string) => {
    const num = parseFloat(raw);
    if (isNaN(num)) {
      onClear?.();
      return;
    }
    if (num < min || num > max) {
      toast.error(`体重请输入合理范围（${min}-${max} kg）`);
      // 恢复原值
      setDraft(value.toFixed(decimal));
      return;
    }
    const clamped = +num.toFixed(decimal);
    setDraft(clamped.toFixed(decimal));
    onChange(clamped);
  };

  const handleBlur = () => {
    if (draft === '') {
      onClear?.();
      return;
    }
    commitValue(draft);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  const applyStep = (delta: number) => {
    const base = empty ? initialValue : value;
    const next = Math.max(min, Math.min(max, +(base + delta).toFixed(decimal)));
    onChange(next);
  };

  return (
    <div className="w-full">
      {/* 数字输入框 */}
      <div className="flex items-center justify-center gap-1 mb-1.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={(e) => {
            const raw = e.target.value;
            // 只允许数字和一个小数点
            if (raw === '' || /^\d*\.?\d{0,1}$/.test(raw)) {
              setDraft(raw);
            }
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="输入体重"
          className="w-20 h-8 text-center font-bold text-xl tabular-nums text-foreground bg-transparent border-b-2 border-primary/30 focus:border-primary outline-none transition-colors placeholder:text-muted-foreground placeholder:text-base"
        />
        <span className="text-[10px] font-normal text-muted-foreground">{unit}</span>
      </div>
      {/* 步进微调按钮 */}
      <div className="flex items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => applyStep(-0.5)}
          disabled={!empty && value - 0.5 < min}
          className="px-1.5 h-6 rounded-full bg-muted text-[10px] font-semibold text-foreground active:scale-90 transition-transform disabled:opacity-30"
        >
          -0.5
        </button>
        <button
          type="button"
          onClick={() => applyStep(-0.1)}
          disabled={!empty && value - 0.1 < min}
          className="w-6 h-6 rounded-full bg-muted flex items-center justify-center active:scale-90 transition-transform disabled:opacity-30"
        >
          <Minus className="w-3 h-3 text-foreground" />
        </button>
        <button
          type="button"
          onClick={() => applyStep(0.1)}
          disabled={!empty && value + 0.1 > max}
          className="w-6 h-6 rounded-full bg-muted flex items-center justify-center active:scale-90 transition-transform disabled:opacity-30"
        >
          <Plus className="w-3 h-3 text-foreground" />
        </button>
        <button
          type="button"
          onClick={() => applyStep(0.5)}
          disabled={!empty && value + 0.5 > max}
          className="px-1.5 h-6 rounded-full bg-muted text-[10px] font-semibold text-foreground active:scale-90 transition-transform disabled:opacity-30"
        >
          +0.5
        </button>
      </div>
    </div>
  );
}

// 内联数字步进器 — 卡片中号
function NumberStepper({
  value,
  onChange,
  min,
  max,
  step,
  unit,
  decimal,
  empty,
  initialValue,
  onFocus,
}: {
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step: number;
  unit: string;
  decimal: number;
  empty: boolean;
  initialValue: number;
  onFocus?: () => void;
}) {
  const displayValue = empty ? '—' : value.toFixed(decimal);

  const handleMinus = () => {
    if (empty) {
      onChange(initialValue);
      return;
    }
    const next = Math.max(min, +(value - step).toFixed(decimal));
    onChange(next);
  };

  const handlePlus = () => {
    if (empty) {
      onChange(initialValue);
      return;
    }
    const next = Math.min(max, +(value + step).toFixed(decimal));
    onChange(next);
  };

  const handleTap = () => {
    if (empty) {
      onChange(initialValue);
      onFocus?.();
    }
  };

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={handleMinus}
        disabled={!empty && value <= min}
        className="w-7 h-7 rounded-full bg-muted flex items-center justify-center active:scale-90 transition-transform disabled:opacity-30"
      >
        <Minus className="w-3.5 h-3.5 text-foreground" />
      </button>
      <button
        type="button"
        onClick={handleTap}
        className={`min-w-[64px] text-center font-bold tabular-nums transition-colors ${
          empty ? 'text-muted-foreground text-lg' : 'text-foreground text-xl'
        }`}
      >
        {displayValue}
        {unit && <span className="text-[10px] font-normal text-muted-foreground ml-0.5">{unit}</span>}
      </button>
      <button
        type="button"
        onClick={handlePlus}
        disabled={!empty && value >= max}
        className="w-7 h-7 rounded-full bg-muted flex items-center justify-center active:scale-90 transition-transform disabled:opacity-30"
      >
        <Plus className="w-3.5 h-3.5 text-foreground" />
      </button>
    </div>
  );
}

export default function HealthCheckInSheet({ trigger, open, onOpenChange }: HealthCheckInSheetProps) {
   const { todayRecord, latestRecord, records, updateToday } = useDailyHealth();
   const { profile } = useUserProfile();
   const { addWeight } = useWeightRecords();

   const [calendarOpen, setCalendarOpen] = useState(false);

   const hasToday = !!todayRecord;

   const [heartRate, setHeartRate] = useState<string>(hasToday && todayRecord.restingHeartRate !== undefined ? String(todayRecord.restingHeartRate) : '');
   const [sleepHours, setSleepHours] = useState<string>(hasToday && todayRecord.sleepHours !== undefined ? String(todayRecord.sleepHours) : '');
   const [weight, setWeight] = useState<string>(hasToday && todayRecord.weight !== undefined ? String(todayRecord.weight) : '');
   const [mood, setMood] = useState<MoodValue | ''>(() => {
     if (!hasToday || !todayRecord.mood) return '';
     const raw = todayRecord.mood as string;
     // 在选项里的直接用，否则是历史已下线值，映射到当前最接近的选项
     if ((MOOD_OPTIONS as { value: string }[]).some(o => o.value === raw)) return raw as MoodValue;
     return LEGACY_MOOD_MAP[raw] ?? '';
   });
   const [submitting, setSubmitting] = useState(false);

   // 上次参考值（最近一次有该字段的记录，不包含今天）
   const lastRef = useMemo(() => {
     if (!latestRecord) return null;
      const isToday = latestRecord.date === formatLocalDate();
     if (!isToday) return latestRecord;
     // latest 就是今天，往前找一条
     const sorted = [...records].sort((a, b) => b.date.localeCompare(a.date));
      return sorted.find(r => r.date !== latestRecord.date) || null;
    }, [latestRecord]);

   // 当日已填项数
   const filledCount = useMemo(() => {
     let c = 0;
     if (heartRate) c++;
     if (sleepHours) c++;
     if (weight) c++;
     if (mood) c++;
     return c;
   }, [heartRate, sleepHours, weight, mood]);

   const allFilled = filledCount === 4;

  const handleSubmit = () => {
    const hr = heartRate ? parseFloat(heartRate) : NaN;
    const sh = sleepHours ? parseFloat(sleepHours) : NaN;
    const w = weight ? parseFloat(weight) : NaN;

    const data: Parameters<typeof updateToday>[0] = {};

    if (!isNaN(hr)) {
      if (hr < 30 || hr > 200) {
        toast.error('静息心率请输入合理范围（30-200 bpm）');
        return;
      }
      data.restingHeartRate = hr;
    }
    if (!isNaN(sh)) {
      if (sh < 1 || sh > 20) {
        toast.error('睡眠时长请输入合理范围（1-20 h）');
        return;
      }
      data.sleepHours = sh;
    }
    if (!isNaN(w)) {
      if (w < 30 || w > 300) {
        toast.error('体重请输入合理范围（30-300 kg）');
        return;
      }
      data.weight = w;
    }
    if (mood) {
      const moodOpt = MOOD_OPTIONS.find(o => o.value === mood);
      data.mood = moodOpt?.stored ?? 'normal';
    }

    if (Object.keys(data).length === 0) {
      toast.error('请至少填写一项');
      return;
    }

    setSubmitting(true);
    const owner = getCurrentUserId();
    setTimeout(() => {
      if (getCurrentUserId() !== owner) return;
      updateToday(data);
      // 体重同步写入体重趋势记录
      if (data.weight !== undefined) {
        addWeight(data.weight);
      }
      toast.success(`已记录 ${Object.keys(data).length} 项健康数据`);
      setSubmitting(false);
      if (onOpenChange) onOpenChange(false);
    }, 300);
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent side="bottom" className="rounded-t-3xl px-4 pt-3 pb-4 max-h-[88vh] flex flex-col">
        <SheetHeader className="mb-2">
          <div className="flex items-center justify-between">
            <div className="w-8" />
            <SheetTitle className="text-sm">早安打卡 · {new Date().getMonth() + 1}月{new Date().getDate()}日</SheetTitle>
            <button
              type="button"
              onClick={() => setCalendarOpen(true)}
              className="w-8 h-8 rounded-full bg-muted flex items-center justify-center active:scale-95 transition-transform"
              aria-label="查看打卡日历"
            >
              <Calendar className="w-4 h-4 text-foreground" />
            </button>
          </div>
        </SheetHeader>

        {/* 进度条 */}
        <div className="px-1 mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-semibold text-foreground">今日打卡</span>
            <span className="text-[11px] font-bold text-primary">
              {allFilled ? '已完成 ✓' : `已录 ${filledCount}/4`}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-all duration-300"
              style={{ width: `${(filledCount / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* 四格录入区 */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-2 gap-2.5">
            {/* 心率 */}
            <StatCard
              icon={Heart}
              iconBg="bg-primary/15"
              iconColor="text-primary"
              title="静息心率"
              hasValue={!!heartRate}
              lastText={lastRef?.restingHeartRate !== undefined ? `上次 ${lastRef.restingHeartRate} bpm` : '建议晨起测量'}
            >
              <NumberStepper
                value={parseFloat(heartRate) || 62}
                onChange={v => setHeartRate(String(v))}
                min={30}
                max={200}
                step={1}
                unit="bpm"
                decimal={0}
                empty={!heartRate}
                initialValue={62}
              />
            </StatCard>

            {/* 睡眠 */}
            <StatCard
              icon={Moon}
              iconBg="bg-info/15"
              iconColor="text-info"
              title="睡眠时长"
              hasValue={!!sleepHours}
              lastText={lastRef?.sleepHours !== undefined ? `上次 ${lastRef.sleepHours} h` : '推荐 7-9 小时'}
            >
              <NumberStepper
                value={parseFloat(sleepHours) || 7}
                onChange={v => setSleepHours(String(v))}
                min={1}
                max={20}
                step={0.5}
                unit="h"
                decimal={1}
                empty={!sleepHours}
                initialValue={7}
              />
            </StatCard>

            {/* 体重 — 支持直接输入 + 步进微调 */}
            <StatCard
              icon={Scale}
              iconBg="bg-warning/15"
              iconColor="text-warning"
              title="体重"
              hasValue={!!weight}
              lastText={lastRef?.weight !== undefined
                ? `上次 ${lastRef.weight} kg`
                : `基础 ${profile.weight} kg`}
            >
              <WeightInput
                value={parseFloat(weight) || profile.weight}
                onChange={v => setWeight(v.toFixed(1))}
                min={30}
                max={300}
                step={0.1}
                unit="kg"
                decimal={1}
                empty={!weight}
                initialValue={profile.weight}
                onClear={() => setWeight('')}
              />
            </StatCard>

            {/* 心情 */}
            <StatCard
              icon={getMoodMeta(mood).icon}
              iconBg={mood ? getMoodMeta(mood).color + '/20' : 'bg-muted'}
              iconColor={mood ? getMoodMeta(mood).color : 'text-muted-foreground'}
              title={mood ? getMoodMeta(mood).label : '今日心情'}
              hasValue={!!mood}
              lastText={mood ? '点击切换心情' : '选择今天的状态'}
            >
              <div className="w-full flex justify-center">
                <div className="flex gap-1">
                  {MOOD_OPTIONS.map(opt => {
                    const Icon = opt.icon;
                    const active = mood === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setMood(active ? '' : opt.value)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                          active
                            ? opt.color + ' ring-2 ring-current/30'
                            : 'bg-muted text-muted-foreground hover:text-foreground'
                        }`}
                        aria-label={opt.label}
                      >
                        <Icon className="w-4 h-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </StatCard>
          </div>

          {/* 小提示 */}
          <p className="text-[10px] text-muted-foreground text-center mt-4 leading-relaxed">
            坚持每日健康打卡，身体数据将用于训练计划的智能降量与恢复安排
          </p>
        </div>

        {/* 底部固定保存按钮 */}
        <div className="shrink-0 pt-3 mt-1">
          <Button
            onClick={handleSubmit}
            disabled={submitting || filledCount === 0}
            className={`w-full h-11 text-white font-bold text-base ${
              allFilled
                ? 'bg-gradient-to-r from-success to-success/80'
                : 'btn-primary-gradient'
            }`}
          >
            {submitting ? '保存中...' : allFilled ? '全部完成 ✓' : `保存 (${filledCount}/4)`}
          </Button>
        </div>
      </SheetContent>
    </Sheet>

    {/* 日历 Sheet（从打卡 Sheet 内打开） */}
    <HealthCalendarSheet
      open={calendarOpen}
      onOpenChange={setCalendarOpen}
    />
    </>
  );
}
